import { type Theme, Flex, Text, useTheme } from '@shared/components';

interface SummaryStatsCardProps {
  title: string;
  displayValue: string;
  additionalInfo?: string;
  cardColor?: 'success' | 'error';
  /** `data-testid` applied to the display value, for e2e assertions. */
  displayValueTestId?: string;
}

const getCardColors = (theme: Theme, cardColor: 'success' | 'error' | 'default' = 'default') => {
  const colorMapping = {
    error: {
      backgroundColor: theme.palette.error.lighter,
      titleAndInfoColor: theme.palette.error.dark,
      displayColor: theme.palette.error.dark,
    },
    success: {
      backgroundColor: theme.palette.success.lighter,
      titleAndInfoColor: theme.palette.success.dark,
      displayColor: theme.palette.success.dark,
    },
    default: {
      backgroundColor: theme.palette.grey[100],
      titleAndInfoColor: theme.palette.text.secondary,
      displayColor: theme.palette.text.primary,
    },
  };

  return colorMapping[cardColor] ?? colorMapping.default;
};

export default function SummaryStatsCard(props: Readonly<SummaryStatsCardProps>) {
  const { title, displayValue, additionalInfo, cardColor, displayValueTestId } = props;
  const theme = useTheme();

  const { backgroundColor, titleAndInfoColor, displayColor } = getCardColors(theme, cardColor);

  return (
    <Flex
      sx={(theme) => ({
        backgroundColor,
        p: theme.spacing(4),
        borderRadius: theme.shape.rounded(2),
        gap: theme.spacing(4),
        flex: 1,
      })}
    >
      <Flex sx={{ flexDirection: 'column' }}>
        <Text sx={{ color: titleAndInfoColor }}>{title}</Text>
        <Flex sx={(theme) => ({ alignItems: 'baseline', gap: theme.spacing(1) })}>
          <Text
            variant='h3'
            data-testid={displayValueTestId}
            sx={{
              color: displayColor,
            }}
          >
            {displayValue}
          </Text>
          <Text
            variant='caption'
            sx={{
              color: titleAndInfoColor,
            }}
          >
            {additionalInfo}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
}
